import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { CaretLeft, CaretUp, CaretDown, Plus, Trash, FloppyDisk } from "@phosphor-icons/react";
import { invalidateSoftwareConfig } from "@/lib/softwareConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SoftwareCreation() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState("tabs");
  const [registry, setRegistry] = useState(null);
  const [tabs, setTabs] = useState([]);
  const [fields, setFields] = useState({});
  const [delegations, setDelegations] = useState({});
  const [users, setUsers] = useState([]);
  const [mod, setMod] = useState("sales_invoice");
  const [delgUser, setDelgUser] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (user && user.role !== "super_admin") { navigate("/settings"); return; }
    (async () => {
      try {
        const [reg, raw, us] = await Promise.all([
          axios.get(`${API}/software-config/registry`),
          axios.get(`${API}/software-config`),
          axios.get(`${API}/users`).catch(() => ({ data: [] })),
        ]);
        setRegistry(reg.data);
        const savedTabs = {};
        (raw.data.tabs || []).forEach((t) => { if (t.id) savedTabs[t.id] = t; });
        const merged = reg.data.tabs.map((t, i) => ({
          ...t, ...savedTabs[t.id],
          label: savedTabs[t.id]?.label || t.label,
          default_label: t.label,
          visible: savedTabs[t.id]?.visible !== false,
          order: savedTabs[t.id]?.order ?? i,
        }));
        (raw.data.tabs || []).filter((t) => t.custom).forEach((t) => merged.push({ ...t, default_label: "(custom)", visible: t.visible !== false }));
        merged.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
        setTabs(merged);
        setFields(raw.data.fields || {});
        setDelegations(raw.data.delegations || {});
        setUsers((Array.isArray(us.data) ? us.data : []).filter((u) => u.role !== "super_admin"));
      } catch (e) { setMsg(e.response?.data?.detail || "Load failed"); }
    })();
  }, [user, navigate]);

  const save = async (payload) => {
    setSaving(true); setMsg("");
    try {
      await axios.put(`${API}/software-config`, payload);
      invalidateSoftwareConfig();
      setMsg("✓ Saved — changes apply globally to all users");
      setTimeout(() => setMsg(""), 4000);
    } catch (e) { setMsg(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const saveTabs = () => save({ tabs: tabs.map((t, i) => ({ id: t.id, label: t.label, route: t.route, visible: t.visible, order: i, parent: t.parent || null, custom: !!t.custom, external: !!t.external })) });
  const saveFields = () => save({ fields });
  const saveDelegations = () => save({ delegations });

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= tabs.length) return;
    const arr = [...tabs]; [arr[i], arr[j]] = [arr[j], arr[i]]; setTabs(arr);
  };
  const addTab = () => {
    setTabs([...tabs, { id: `custom-${Date.now()}`, custom: true, label: "New Tab", route: "/dashboard", external: false, visible: true, default_label: "(custom)", parent: null }]);
  };

  const fieldRows = useMemo(() => (registry ? registry.fields[mod] || [] : []), [registry, mod]);
  const fVal = (key) => (fields[mod] && fields[mod][key]) || {};
  const setF = (key, patch) => setFields({ ...fields, [mod]: { ...(fields[mod] || {}), [key]: { ...fVal(key), ...patch } } });

  const delgFields = (delegations[delgUser] && delegations[delgUser].fields) || {};
  const toggleDelg = (fk) => {
    const nf = { ...delgFields };
    if (nf[fk]) delete nf[fk]; else nf[fk] = true;
    setDelegations({ ...delegations, [delgUser]: { ...(delegations[delgUser] || {}), fields: nf } });
  };

  if (!registry) return <div className="p-10 text-slate-500" data-testid="sc-loading">Loading Software Creation…</div>;

  return (
    <div className="max-w-6xl" data-testid="software-creation-page">
      <div className="mb-8 flex items-center gap-3">
        <button onClick={() => navigate("/settings")} data-testid="sc-back" className="p-2 border border-slate-300 rounded-sm hover:bg-slate-100 text-slate-700"><CaretLeft size={18} weight="bold" /></button>
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-slate-900">Software Creation</h1>
          <p className="text-slate-500 text-sm">Super Admin only — tabs, links, fields & delegation. Changes apply globally to all users.</p>
        </div>
      </div>

      <div className="flex gap-1 bg-slate-100 p-1 rounded-sm mb-6 w-fit">
        {[["tabs", "Tabs & Links"], ["fields", "Fields & Columns"], ["delegation", "Delegation"]].map(([k, l]) => (
          <button key={k} onClick={() => setSection(k)} data-testid={`sc-section-${k}`}
            className={`px-4 py-2 rounded-sm text-sm font-bold ${section === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-white"}`}>{l}</button>
        ))}
      </div>

      {msg && <div className="mb-4 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-sm text-sm font-semibold text-emerald-800" data-testid="sc-msg">{msg}</div>}

      {section === "tabs" && (
        <div className="bg-white border border-slate-200 rounded-sm" data-testid="sc-tabs-panel">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <p className="text-sm font-bold text-slate-700">Every tab & tool — rename, show/hide, reorder, or add new tabs linked to existing screens / external links</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={addTab} data-testid="sc-add-tab"><Plus size={14} weight="bold" /> Add Tab</Button>
              <Button size="sm" onClick={saveTabs} disabled={saving} data-testid="sc-save-tabs" className="bg-slate-900 text-white font-bold"><FloppyDisk size={14} weight="bold" /> Save Tabs</Button>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-900 text-white text-left text-xs uppercase">
              <th className="px-3 py-2">Tab Name (editable)</th><th className="px-3 py-2">Connected Link / Route</th><th className="px-3 py-2">Sub-tab of</th><th className="px-3 py-2 text-center">Visible</th><th className="px-3 py-2 text-center">Order</th><th className="px-3 py-2"></th>
            </tr></thead>
            <tbody>
              {tabs.map((t, i) => (
                <tr key={t.id} className="border-b border-slate-100" data-testid={`sc-tab-row-${t.id}`}>
                  <td className="px-3 py-2">
                    <Input value={t.label} onChange={(e) => setTabs(tabs.map((x, xi) => xi === i ? { ...x, label: e.target.value } : x))} data-testid={`sc-tab-label-${t.id}`} className="h-8" />
                    <p className="text-[10px] text-slate-400 mt-0.5">{t.tool ? "Tool" : t.custom ? "Custom tab" : `Default: ${t.default_label}`}</p>
                  </td>
                  <td className="px-3 py-2">
                    {t.custom ? (
                      <Input value={t.route} onChange={(e) => setTabs(tabs.map((x, xi) => xi === i ? { ...x, route: e.target.value, external: /^https?:/i.test(e.target.value) } : x))} placeholder="/invoices ya https://..." data-testid={`sc-tab-route-${t.id}`} className="h-8 font-mono text-xs" />
                    ) : (
                      <code className="text-xs text-slate-600 bg-slate-50 px-2 py-1 rounded-sm">{t.route}</code>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <select value={t.parent || ""} onChange={(e) => setTabs(tabs.map((x, xi) => xi === i ? { ...x, parent: e.target.value || null } : x))}
                      className="h-8 px-2 border border-slate-200 rounded-sm text-xs bg-white" data-testid={`sc-tab-parent-${t.id}`}>
                      <option value="">— Top level —</option>
                      {tabs.filter((x) => x.id !== t.id && !x.parent).map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-center"><Switch checked={t.visible} onCheckedChange={(v) => setTabs(tabs.map((x, xi) => xi === i ? { ...x, visible: v } : x))} data-testid={`sc-tab-visible-${t.id}`} /></td>
                  <td className="px-3 py-2 text-center whitespace-nowrap">
                    <button onClick={() => move(i, -1)} className="p-1 text-slate-500 hover:text-slate-900" data-testid={`sc-tab-up-${t.id}`}><CaretUp size={14} weight="bold" /></button>
                    <button onClick={() => move(i, 1)} className="p-1 text-slate-500 hover:text-slate-900" data-testid={`sc-tab-down-${t.id}`}><CaretDown size={14} weight="bold" /></button>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {t.custom && <button onClick={() => setTabs(tabs.filter((_, xi) => xi !== i))} className="p-1 text-rose-500 hover:text-rose-700" data-testid={`sc-tab-delete-${t.id}`}><Trash size={14} weight="bold" /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {section === "fields" && (
        <div className="bg-white border border-slate-200 rounded-sm" data-testid="sc-fields-panel">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Label className="text-xs font-bold">Module</Label>
              <select value={mod} onChange={(e) => setMod(e.target.value)} data-testid="sc-module-select"
                className="h-9 px-3 border border-slate-200 rounded-sm text-sm bg-white font-semibold">
                {Object.keys(registry.fields).map((m) => <option key={m} value={m}>{m.replace(/_/g, " ").toUpperCase()}</option>)}
              </select>
              <p className="text-xs text-slate-500">Toggle ON = admins edit kar sakte hain · OFF = locked / auto-calculated</p>
            </div>
            <Button size="sm" onClick={saveFields} disabled={saving} data-testid="sc-save-fields" className="bg-slate-900 text-white font-bold"><FloppyDisk size={14} weight="bold" /> Save Fields</Button>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-900 text-white text-left text-xs uppercase">
              <th className="px-3 py-2">Default Column/Field</th><th className="px-3 py-2">Display Name (rename)</th><th className="px-3 py-2 text-center">Editable (ON) / Locked-Auto (OFF)</th>
            </tr></thead>
            <tbody>
              {fieldRows.map((f) => {
                const v = fVal(f.key);
                const editable = v.editable !== undefined ? v.editable : f.editable;
                return (
                  <tr key={f.key} className="border-b border-slate-100" data-testid={`sc-field-row-${mod}-${f.key}`}>
                    <td className="px-3 py-2 font-semibold text-slate-700">{f.label}{f.auto && <span className="ml-2 text-[9px] font-black px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800">AUTO</span>}</td>
                    <td className="px-3 py-2"><Input value={v.label ?? f.label} onChange={(e) => setF(f.key, { label: e.target.value })} data-testid={`sc-field-label-${mod}-${f.key}`} className="h-8" /></td>
                    <td className="px-3 py-2 text-center"><Switch checked={!!editable} onCheckedChange={(val) => setF(f.key, { editable: val })} data-testid={`sc-field-editable-${mod}-${f.key}`} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {section === "delegation" && (
        <div className="bg-white border border-slate-200 rounded-sm" data-testid="sc-delegation-panel">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Label className="text-xs font-bold">Admin</Label>
              <select value={delgUser} onChange={(e) => setDelgUser(e.target.value.toLowerCase())} data-testid="sc-delg-user"
                className="h-9 px-3 border border-slate-200 rounded-sm text-sm bg-white font-semibold min-w-[260px]">
                <option value="">— Select admin/user —</option>
                {users.map((u) => <option key={u.email} value={(u.email || "").toLowerCase()}>{u.name || u.email} ({u.role})</option>)}
              </select>
              <p className="text-xs text-slate-500">Delegated fields sirf isi admin ke liye editable honge (global OFF hone par bhi)</p>
            </div>
            <Button size="sm" onClick={saveDelegations} disabled={saving || !delgUser} data-testid="sc-save-delegations" className="bg-slate-900 text-white font-bold"><FloppyDisk size={14} weight="bold" /> Save Delegation</Button>
          </div>
          {delgUser ? (
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(registry.fields).map(([m, fl]) => (
                <div key={m} className="border border-slate-200 rounded-sm p-3">
                  <p className="text-xs font-black uppercase tracking-wider text-slate-500 mb-2">{m.replace(/_/g, " ")}</p>
                  {fl.map((f) => {
                    const fk = `${m}.${f.key}`;
                    return (
                      <label key={fk} className="flex items-center gap-2 py-1 cursor-pointer text-sm" data-testid={`sc-delg-${fk}`}>
                        <input type="checkbox" checked={!!delgFields[fk]} onChange={() => toggleDelg(fk)} className="h-4 w-4" />
                        <span className="text-slate-700">{f.label}</span>
                      </label>
                    );
                  })}
                </div>
              ))}
            </div>
          ) : (
            <p className="p-8 text-center text-slate-400 text-sm">Select an admin above to delegate field-edit permissions</p>
          )}
        </div>
      )}
    </div>
  );
}
